/**
 * TRS-03 — reading the FICA review clock on the client's screens.
 *
 * THE RULE (Paul). A trust with no initial FICA on file has no review date and
 * no countdown, on every surface. It says the initial FICA is outstanding and
 * offers the way to do it. The periodic clock exists only once a first
 * verification is on file.
 *
 * The backend now withholds `nextFicaReview` and `ficaDaysOut` on such a trust
 * (`backend/src/lib/fica-review-clock.ts`), so the honest render is what these
 * helpers give you. THE TRAP THEY EXIST TO CLOSE: `t.ficaDaysOut ?? 0` reads as
 * "due today" and `t.ficaDaysOut ?? 0 <= 0` as "overdue" — a missing clock
 * defaulted to a number is worse than the bug it replaces. Never default it.
 * Ask `hasFicaReviewClock` first, or sort with `ficaSortKey`.
 */

export interface FicaClockSource {
  nextFicaReview?: string | null;
  ficaDaysOut?: number | null;
  /** `false` = no initial FICA. `null`/absent = unknown, which keeps the clock. */
  initialFicaOnFile?: boolean | null;
}

/** True when this trust has a real periodic review clock to show. False when
 *  the initial FICA is outstanding, or the backend sent no countdown. */
export function hasFicaReviewClock(t: FicaClockSource): boolean {
  if (t.initialFicaOnFile === false) return false;
  return typeof t.ficaDaysOut === 'number';
}

/** The day count, or `undefined` when there is no clock. Screens branch on the
 *  `undefined`; they never substitute a number for it. */
export function ficaDaysOutOf(t: FicaClockSource): number | undefined {
  return hasFicaReviewClock(t) ? (t.ficaDaysOut as number) : undefined;
}

/** The review date, or `undefined` when there is no clock. */
export function ficaReviewDateOf(t: FicaClockSource): string | undefined {
  return hasFicaReviewClock(t) ? (t.nextFicaReview ?? undefined) : undefined;
}

/** True when the trust's initial FICA is outstanding — the thing to say instead
 *  of a countdown. Distinct from "clock unknown": an unreadable register is not
 *  a claim that nobody was verified. */
export function isInitialFicaOutstanding(t: FicaClockSource): boolean {
  return t.initialFicaOnFile === false;
}

/** Sort key for a worst-first FICA list, ascending. Initial-FICA work sorts
 *  ahead of every periodic row; a row with no clock at all sorts last rather
 *  than to zero. FINITE on purpose: two `-Infinity` keys subtract to `NaN`,
 *  which leaves `Array.prototype.sort` free to return any order it likes. */
export function ficaSortKey(t: FicaClockSource): number {
  if (isInitialFicaOutstanding(t)) return Number.MIN_SAFE_INTEGER;
  return ficaDaysOutOf(t) ?? Number.MAX_SAFE_INTEGER;
}

// ── THE WHOLE REGISTER (RULING 2026-09-15, Paul) ────────────────────────────
//
// A trust's initial FICA is on file ONLY when EVERY person on its register
// carries a verified status. One verified person out of several does not make
// the trust compliant, and an EMPTY register is never vacuously on file.
//
// The server decides this in `backend/src/lib/firm/book-summary.ts` and sends
// it as `initialFicaOnFile`, which `isInitialFicaOutstanding` above reads. The
// trust overview ALSO derives it from the register it already holds, so that
// the screen is right before the trust query settles. That second derivation
// used to be `received === 0` - the exact threshold the round-6 grader failed
// on the wire - so it lives here now, in one function, rather than inline in a
// screen where the next edit can drift from the server's rule.

export interface FicaRegisterCounts {
  /** People on the register whose FICA is verified. */
  verified: number;
  /** People on the register, full stop. */
  total: number;
}

/** True when the trust's initial FICA is still outstanding: nobody on the
 *  register, or anybody on it not yet verified. */
export function initialFicaOutstandingOnRegister(counts: FicaRegisterCounts): boolean {
  return counts.total === 0 || counts.verified < counts.total;
}

/**
 * "2 of 4 people verified" - what a partly-done register says instead of a
 * bare "outstanding", so the firm can see the work it has already done.
 *
 * `undefined` when there is nothing yet to report (an empty register, or
 * nobody verified on it): the screens have their own sentence for those, and a
 * "0 of 4 people verified" beside a list of four outstanding names says the
 * same thing twice.
 */
export function ficaVerifiedPhrase(counts: FicaRegisterCounts): string | undefined {
  if (counts.total === 0 || counts.verified === 0) return undefined;
  return `${counts.verified} of ${counts.total} ${counts.total === 1 ? 'person' : 'people'} verified`;
}
